import React from 'react';
import {styled} from '@mui/material/styles';
import {AiOutlineLeft, AiOutlineRight} from 'react-icons/ai';

// A 44px tap target around a 36px circle, so a thumb that misses the circle slightly
// still moves the photo instead of opening the location (securspace#3503).
// No zIndex here or on the count: being positioned and after the photo already puts them on
// top of it, and a zIndex lifted them over the search box's suggestions too (securspace-ui#3201).
const NavButton = styled('button')(() => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  width: '44px',
  height: '44px',
  padding: 0,
  border: 'none',
  background: 'none',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  '& > span': {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    color: '#324559',
    // Keeps the white circle visible on bright photos.
    boxShadow: '0 1px 3px rgba(20, 30, 48, 0.28)',
    transition: 'background-color 0.18s ease',
  },
  '&:hover:not([aria-disabled="true"]) > span': {
    backgroundColor: 'white',
  },
  '&[aria-disabled="true"]': {
    cursor: 'not-allowed',
  },
  '&[aria-disabled="true"] > span': {
    backgroundColor: 'rgba(165, 164, 164, 0.7)',
    color: '#999',
    boxShadow: 'none',
  },
  '&:focus-visible': {
    outline: 'none',
  },
  '&:focus-visible > span': {
    outline: '3px solid #F79019',
    outlineOffset: '2px',
  },
}));

const PrevButton = styled(NavButton)(() => ({left: '4px'}));

const NextButton = styled(NavButton)(() => ({right: '4px'}));

const PhotoCount = styled('span')(() => ({
  position: 'absolute',
  right: '10px',
  bottom: '10px',
  padding: '3px 8px',
  borderRadius: '4px',
  backgroundColor: 'rgba(20, 30, 48, 0.68)',
  color: 'white',
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '16px',
  fontVariantNumeric: 'tabular-nums',
  pointerEvents: 'none',
}));

interface LocationImageNavProps {
  activeSlide: number;
  totalImages: number;
  onPrev: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onNext: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Previous / next arrows and an "n / total" count over a location card's photo.
 * Place it inside the photo's positioned box. The ends use aria-disabled, not disabled:
 * a disabled button runs no click handler, so on the search list the tap fell through
 * to the card's link and opened the location page (securspace#3503).
 */
const LocationImageNav = ({activeSlide, totalImages, onPrev, onNext}: LocationImageNavProps) => {
  if (totalImages < 2) return null;
  const atStart = activeSlide === 0;
  const atEnd = activeSlide === totalImages - 1;

  return <>
    <PrevButton type="button" aria-label="Previous photo" aria-disabled={atStart} onClick={onPrev}>
      <span><AiOutlineLeft size={18}/></span>
    </PrevButton>
    <NextButton type="button" aria-label="Next photo" aria-disabled={atEnd} onClick={onNext}>
      <span><AiOutlineRight size={18}/></span>
    </NextButton>
    <PhotoCount aria-hidden="true">{activeSlide + 1} / {totalImages}</PhotoCount>
  </>;
};

export default LocationImageNav;
